import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

export type BuiltInCameraPreset = 'front-machine' | 'playfield-close-up' | 'prize-tray';
export type CameraPresetName = BuiltInCameraPreset | (string & {});
export type CameraVector = THREE.Vector3 | readonly [number, number, number];
export type CameraEasing = (progress: number) => number;

export interface CameraPose {
  position: CameraVector;
  target: CameraVector;
  fov?: number;
}

export interface CameraOrbitConstraints {
  minDistance: number;
  maxDistance: number;
  minPolarAngle: number;
  maxPolarAngle: number;
  minAzimuthAngle: number;
  maxAzimuthAngle: number;
}

export interface CameraRigOptions {
  domElement?: HTMLElement | null;
  presets?: Record<string, CameraPose>;
  homePreset?: CameraPresetName;
  initialPreset?: CameraPresetName | null;
  constraints?: Partial<CameraOrbitConstraints>;
  defaultTransitionDuration?: number;
  dampingFactor?: number;
  rotateSpeed?: number;
  zoomSpeed?: number;
  enablePan?: boolean;
  respectReducedMotion?: boolean;
}

export interface CameraTransitionOptions {
  duration?: number;
  easing?: CameraEasing;
  onComplete?: () => void;
  onCancel?: () => void;
}

export interface CameraShakeOptions {
  /** Maximum screen-space displacement in CSS pixels. */
  strength?: number;
  duration?: number;
  frequency?: number;
}

export interface CameraRigState {
  position: THREE.Vector3;
  target: THREE.Vector3;
  distance: number;
  polarAngle: number;
  azimuthAngle: number;
  activePreset: CameraPresetName | null;
  transitioning: boolean;
  following: boolean;
}

interface StoredPose {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov: number;
}

interface ActiveTransition {
  elapsed: number;
  duration: number;
  from: StoredPose;
  to: StoredPose;
  easing: CameraEasing;
  controlsWereEnabled: boolean;
  preset: CameraPresetName | null;
  onComplete?: () => void;
  onCancel?: () => void;
}

interface ViewOffsetSnapshot {
  fullWidth: number;
  fullHeight: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

const DEFAULT_CONSTRAINTS: CameraOrbitConstraints = {
  minDistance: 3.2,
  maxDistance: 16,
  minPolarAngle: THREE.MathUtils.degToRad(22),
  maxPolarAngle: THREE.MathUtils.degToRad(84),
  minAzimuthAngle: THREE.MathUtils.degToRad(-65),
  maxAzimuthAngle: THREE.MathUtils.degToRad(65),
};

// The cabinet is centred at its footprint, faces +Z and is approximately
// 5.2w x 5.8h x 3.5d. Every pose can be replaced through CameraRigOptions.
const DEFAULT_PRESETS: Record<BuiltInCameraPreset, CameraPose> = {
  'front-machine': {
    position: [2.4, 5, 9.6],
    target: [0, 3, 0.1],
    fov: 40,
  },
  'playfield-close-up': {
    position: [1.25, 6.7, 5.8],
    target: [-0.08, 2.5, 0.34],
    fov: 31,
  },
  'prize-tray': {
    position: [1.25, 3.05, 6.2],
    target: [0, 1.35, 1.78],
    fov: 32,
  },
};

const DEFAULT_LEGACY_OFFSET = new THREE.Vector3(0, 9.5, 9.5);
const DEFAULT_EASING: CameraEasing = (progress) =>
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

function copyVector(target: THREE.Vector3, source: CameraVector): THREE.Vector3 {
  return Array.isArray(source)
    ? target.set(source[0], source[1], source[2])
    : target.copy(source as THREE.Vector3);
}

function isVector3(value: unknown): value is THREE.Vector3 {
  return Boolean(value && typeof value === 'object' && 'isVector3' in value);
}

function isDomElement(value: unknown): value is HTMLElement {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'addEventListener' in value &&
      'clientWidth' in value,
  );
}

/**
 * Desktop exploration camera for the coin-pusher cabinet.
 *
 * New integrations should construct it with the render canvas and call
 * `update(deltaSeconds)` once per frame. The Vector3 constructor overload and
 * `snapTo`/`update(delta, target, lag)` remain compatible with the starter game.
 */
export class CameraRig {
  readonly controls: OrbitControls;

  private readonly presets = new Map<CameraPresetName, StoredPose>();
  private readonly desiredPosition = new THREE.Vector3();
  private readonly desiredTarget = new THREE.Vector3();
  private readonly legacyOffset = DEFAULT_LEGACY_OFFSET.clone();
  private domElement: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private transition: ActiveTransition | null = null;
  private homePreset: CameraPresetName;
  private activePreset: CameraPresetName | null = null;
  private legacyFollowing = false;
  private disposed = false;
  private viewportWidth = 1;
  private viewportHeight = 1;
  private readonly defaultTransitionDuration: number;
  private readonly respectReducedMotion: boolean;

  private shakeStrength = 0;
  private shakeDuration = 0;
  private shakeRemaining = 0;
  private shakeElapsed = 0;
  private shakeFrequency = 27;
  private shakeBaseView: ViewOffsetSnapshot | null = null;

  constructor(camera: THREE.PerspectiveCamera, offset?: THREE.Vector3);
  constructor(camera: THREE.PerspectiveCamera, options?: CameraRigOptions);
  constructor(
    camera: THREE.PerspectiveCamera,
    domElement: HTMLElement | null,
    options?: CameraRigOptions,
  );
  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    second?: THREE.Vector3 | CameraRigOptions | HTMLElement | null,
    third: CameraRigOptions = {},
  ) {
    let options: CameraRigOptions;
    if (isVector3(second)) {
      this.legacyOffset.copy(second);
      options = {};
    } else if (isDomElement(second) || second === null) {
      options = { ...third, domElement: second };
    } else {
      options = second ?? {};
    }

    this.homePreset = options.homePreset ?? 'front-machine';
    this.defaultTransitionDuration = Math.max(0, options.defaultTransitionDuration ?? 0.85);
    this.respectReducedMotion = options.respectReducedMotion ?? true;

    for (const [name, pose] of Object.entries(DEFAULT_PRESETS)) this.setPreset(name, pose);
    for (const [name, pose] of Object.entries(options.presets ?? {})) this.setPreset(name, pose);

    const limits = { ...DEFAULT_CONSTRAINTS, ...options.constraints };
    this.controls = new OrbitControls(camera, null);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = options.dampingFactor ?? 0.075;
    this.controls.rotateSpeed = options.rotateSpeed ?? 0.62;
    this.controls.zoomSpeed = options.zoomSpeed ?? 0.9;
    this.controls.enablePan = options.enablePan ?? false;
    this.controls.screenSpacePanning = false;
    this.controls.zoomToCursor = false;
    this.controls.minDistance = limits.minDistance;
    this.controls.maxDistance = limits.maxDistance;
    this.controls.minPolarAngle = limits.minPolarAngle;
    this.controls.maxPolarAngle = limits.maxPolarAngle;
    this.controls.minAzimuthAngle = limits.minAzimuthAngle;
    this.controls.maxAzimuthAngle = limits.maxAzimuthAngle;
    this.controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
    this.controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
    this.controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
    this.controls.addEventListener('start', this.handleOrbitStart);

    const initialPreset = options.initialPreset === undefined ? this.homePreset : options.initialPreset;
    if (initialPreset && !this.snapToPreset(initialPreset)) this.snapToPreset('front-machine');
    this.controls.saveState();

    if (options.domElement) this.connect(options.domElement);
  }

  connect(element: HTMLElement): void {
    if (this.disposed) return;
    this.disconnect();
    this.domElement = element;
    this.controls.connect(element);
    element.addEventListener('pointerdown', this.handleTransitionInterrupt, true);
    element.addEventListener('wheel', this.handleTransitionInterrupt, { capture: true, passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (rect) this.resize(rect.width, rect.height);
      });
      this.resizeObserver.observe(element);
    }
    this.resize(element.clientWidth, element.clientHeight);
  }

  disconnect(): void {
    if (this.domElement) {
      this.domElement.removeEventListener('pointerdown', this.handleTransitionInterrupt, true);
      this.domElement.removeEventListener('wheel', this.handleTransitionInterrupt, true);
      this.controls.disconnect();
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.domElement = null;
  }

  resize(width?: number, height?: number): void {
    const nextWidth = width ?? this.domElement?.clientWidth ?? this.viewportWidth;
    const nextHeight = height ?? this.domElement?.clientHeight ?? this.viewportHeight;
    if (!Number.isFinite(nextWidth) || !Number.isFinite(nextHeight) || nextWidth <= 0 || nextHeight <= 0) {
      return;
    }

    this.viewportWidth = nextWidth;
    this.viewportHeight = nextHeight;
    const aspect = nextWidth / nextHeight;
    if (Math.abs(this.camera.aspect - aspect) > 0.0001) {
      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
    }
  }

  setPreset(name: CameraPresetName, pose: CameraPose): void {
    const stored: StoredPose = {
      position: copyVector(new THREE.Vector3(), pose.position),
      target: copyVector(new THREE.Vector3(), pose.target),
      fov: pose.fov ?? this.camera.fov,
    };
    this.presets.set(name, stored);
  }

  getPreset(name: CameraPresetName): CameraPose | null {
    const pose = this.presets.get(name);
    return pose
      ? { position: pose.position.clone(), target: pose.target.clone(), fov: pose.fov }
      : null;
  }

  listPresets(): CameraPresetName[] {
    return [...this.presets.keys()];
  }

  setHomePreset(name: CameraPresetName): boolean {
    if (!this.presets.has(name)) return false;
    this.homePreset = name;
    return true;
  }

  focusPreset(name: CameraPresetName, options: CameraTransitionOptions = {}): boolean {
    const pose = this.presets.get(name);
    if (!pose) return false;
    this.startTransition(pose, options, name);
    return true;
  }

  snapToPreset(name: CameraPresetName): boolean {
    const pose = this.presets.get(name);
    if (!pose) return false;
    this.cancelTransition();
    this.legacyFollowing = false;
    this.applyPose(pose);
    this.activePreset = name;
    return true;
  }

  transitionTo(pose: CameraPose, options: CameraTransitionOptions = {}): void {
    const stored: StoredPose = {
      position: copyVector(new THREE.Vector3(), pose.position),
      target: copyVector(new THREE.Vector3(), pose.target),
      fov: pose.fov ?? this.camera.fov,
    };
    this.startTransition(stored, options, null);
  }

  snapToPose(pose: CameraPose): void {
    this.cancelTransition();
    this.legacyFollowing = false;
    this.activePreset = null;
    this.applyPose({
      position: copyVector(new THREE.Vector3(), pose.position),
      target: copyVector(new THREE.Vector3(), pose.target),
      fov: pose.fov ?? this.camera.fov,
    });
  }

  home(options: CameraTransitionOptions = {}): boolean {
    return this.focusPreset(this.homePreset, options);
  }

  reset(options: CameraTransitionOptions = {}): boolean {
    return this.home(options);
  }

  cancelTransition(): void {
    const active = this.transition;
    if (!active) return;
    this.transition = null;
    this.controls.enabled = active.controlsWereEnabled;
    active.onCancel?.();
  }

  /** Adds a short, capped screen-space impulse without disturbing orbit state. */
  shake(options: CameraShakeOptions | number = {}): void {
    if (this.prefersReducedMotion()) return;
    const settings = typeof options === 'number' ? { strength: options } : options;
    const strength = THREE.MathUtils.clamp(settings.strength ?? 7, 0, 18);
    const duration = THREE.MathUtils.clamp(settings.duration ?? 0.22, 0, 0.7);
    if (strength === 0 || duration === 0) return;

    if (this.shakeRemaining <= 0) this.captureViewOffset();
    this.shakeStrength = Math.min(18, Math.hypot(this.shakeStrength, strength));
    this.shakeDuration = Math.max(this.shakeDuration, duration);
    this.shakeRemaining = Math.max(this.shakeRemaining, duration);
    this.shakeFrequency = THREE.MathUtils.clamp(settings.frequency ?? 27, 8, 45);
    this.shakeElapsed = 0;
  }

  addShakeImpulse(strength = 7, duration = 0.22): void {
    this.shake({ strength, duration });
  }

  /** Starter-game compatibility: snap to a moving target using the legacy offset. */
  snapTo(target: THREE.Vector3): void {
    this.cancelTransition();
    this.legacyFollowing = true;
    this.activePreset = null;
    this.camera.position.copy(target).add(this.legacyOffset);
    this.controls.target.copy(target).add(this.desiredTarget.set(0, 0.4, 0));
    this.controls.cursor.copy(this.controls.target);
    this.camera.lookAt(this.controls.target);
    this.controls.update(0);
  }

  stopFollowing(): void {
    this.legacyFollowing = false;
  }

  update(deltaSeconds: number, target?: THREE.Vector3, lag = 0.16): void {
    if (this.disposed) return;
    const delta = THREE.MathUtils.clamp(deltaSeconds, 0, 0.1);

    if (this.transition) {
      this.updateTransition(delta);
    } else if (this.legacyFollowing && target) {
      this.updateLegacyFollow(delta, target, lag);
    } else {
      this.controls.update(delta);
    }

    this.updateShake(delta);
  }

  getState(): CameraRigState {
    return {
      position: this.camera.position.clone(),
      target: this.controls.target.clone(),
      distance: this.controls.getDistance(),
      polarAngle: this.controls.getPolarAngle(),
      azimuthAngle: this.controls.getAzimuthalAngle(),
      activePreset: this.activePreset,
      transitioning: this.transition !== null,
      following: this.legacyFollowing,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.cancelTransition();
    this.clearShake();
    this.disconnect();
    this.controls.removeEventListener('start', this.handleOrbitStart);
    this.controls.dispose();
    this.disposed = true;
  }

  private startTransition(
    pose: StoredPose,
    options: CameraTransitionOptions,
    preset: CameraPresetName | null,
  ): void {
    this.cancelTransition();
    this.legacyFollowing = false;
    const duration = Math.max(0, options.duration ?? this.defaultTransitionDuration);
    if (duration === 0 || this.prefersReducedMotion()) {
      this.applyPose(pose);
      this.activePreset = preset;
      options.onComplete?.();
      return;
    }

    const controlsWereEnabled = this.controls.enabled;
    this.transition = {
      elapsed: 0,
      duration,
      from: this.capturePose(),
      to: {
        position: pose.position.clone(),
        target: pose.target.clone(),
        fov: pose.fov,
      },
      easing: options.easing ?? DEFAULT_EASING,
      controlsWereEnabled,
      preset,
      onComplete: options.onComplete,
      onCancel: options.onCancel,
    };
    this.controls.enabled = false;
    this.activePreset = null;
  }

  private updateTransition(delta: number): void {
    const active = this.transition;
    if (!active) return;
    active.elapsed = Math.min(active.duration, active.elapsed + delta);
    const linearProgress = active.duration === 0 ? 1 : active.elapsed / active.duration;
    const progress = THREE.MathUtils.clamp(active.easing(linearProgress), 0, 1);

    this.camera.position.lerpVectors(active.from.position, active.to.position, progress);
    this.controls.target.lerpVectors(active.from.target, active.to.target, progress);
    this.camera.fov = THREE.MathUtils.lerp(active.from.fov, active.to.fov, progress);
    this.camera.lookAt(this.controls.target);
    this.camera.updateProjectionMatrix();

    if (linearProgress < 1) return;
    this.transition = null;
    this.applyPose(active.to);
    this.controls.enabled = active.controlsWereEnabled;
    this.activePreset = active.preset;
    active.onComplete?.();
  }

  private updateLegacyFollow(delta: number, target: THREE.Vector3, lag: number): void {
    this.desiredPosition.copy(target).add(this.legacyOffset);
    const factor = 1 - Math.exp(-delta / Math.max(0.001, lag));
    this.camera.position.lerp(this.desiredPosition, factor);
    this.controls.target.copy(target).add(this.desiredTarget.set(0, 0.35, -1.2));
    this.controls.cursor.copy(this.controls.target);
    this.camera.lookAt(this.controls.target);
    this.controls.update(delta);
  }

  private capturePose(): StoredPose {
    return {
      position: this.camera.position.clone(),
      target: this.controls.target.clone(),
      fov: this.camera.fov,
    };
  }

  private applyPose(pose: StoredPose): void {
    this.camera.position.copy(pose.position);
    this.controls.target.copy(pose.target);
    this.controls.cursor.copy(pose.target);
    this.camera.fov = pose.fov;
    this.camera.lookAt(pose.target);
    this.camera.updateProjectionMatrix();
    this.controls.update(0);
  }

  private prefersReducedMotion(): boolean {
    return (
      this.respectReducedMotion &&
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  private captureViewOffset(): void {
    const view = this.camera.view;
    this.shakeBaseView =
      view?.enabled
        ? {
            fullWidth: view.fullWidth,
            fullHeight: view.fullHeight,
            offsetX: view.offsetX,
            offsetY: view.offsetY,
            width: view.width,
            height: view.height,
          }
        : null;
  }

  private updateShake(delta: number): void {
    if (this.shakeRemaining <= 0) return;
    this.shakeElapsed += delta;
    this.shakeRemaining = Math.max(0, this.shakeRemaining - delta);
    const envelope = Math.pow(this.shakeRemaining / Math.max(0.001, this.shakeDuration), 2);
    const phase = this.shakeElapsed * this.shakeFrequency * Math.PI * 2;
    const x = (Math.sin(phase) + Math.sin(phase * 2.17 + 1.7) * 0.35) * this.shakeStrength * envelope;
    const y = (Math.sin(phase * 1.31 + 2.4) + Math.sin(phase * 2.73) * 0.3) * this.shakeStrength * envelope;
    const base = this.shakeBaseView;

    if (base) {
      this.camera.setViewOffset(
        base.fullWidth,
        base.fullHeight,
        base.offsetX + x,
        base.offsetY + y,
        base.width,
        base.height,
      );
    } else {
      const width = Math.max(1, this.viewportWidth);
      const height = Math.max(1, this.viewportHeight);
      this.camera.setViewOffset(width, height, x, y, width, height);
    }

    if (this.shakeRemaining === 0) this.clearShake();
  }

  private clearShake(): void {
    const base = this.shakeBaseView;
    if (base) {
      this.camera.setViewOffset(
        base.fullWidth,
        base.fullHeight,
        base.offsetX,
        base.offsetY,
        base.width,
        base.height,
      );
    } else {
      this.camera.clearViewOffset();
    }
    this.shakeBaseView = null;
    this.shakeStrength = 0;
    this.shakeDuration = 0;
    this.shakeRemaining = 0;
    this.shakeElapsed = 0;
  }

  private readonly handleTransitionInterrupt = (): void => {
    this.cancelTransition();
    this.activePreset = null;
  };

  private readonly handleOrbitStart = (): void => {
    this.activePreset = null;
    this.legacyFollowing = false;
  };
}
